import { useState } from 'react';

import styled from 'styled-components';

import { ActionText } from '@components/action-text';
import { Checkbox } from '@components/checkbox';
import { createForm, useCreateFormStore } from '@components/form';
import {
  PasswordStrengthBar,
  usePasswordValidations,
} from '@components/password-strength-bar/password-strength-bar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { UnauthedPage } from 'components/unauthed-page';
import { useStore } from 'models/Provider';

type SetPasswordData = {
  password1: string;
  password2: string;
};

const Form = createForm<SetPasswordData>();

type SetPasswordProps = {
  onSubmit: (data: SetPasswordData) => void;
  disableSubmit: boolean;
  isReset: boolean;
};

export const SetPassword = (props: SetPasswordProps) => {
  const { onSubmit, disableSubmit, isReset } = props;
  const { t } = useScopedTranslate(locale);
  const [isChecked, setIsChecked] = useState(false);
  const [hasPressedSend, setHasPressedSend] = useState(false);

  const formStore = useCreateFormStore();
  const formValues = formStore.useFormValues();
  const password1 = (formValues['password1'] as string) ?? '';
  const password2 = (formValues['password2'] as string) ?? '';

  const { isAllValid, ValidationMessages } = usePasswordValidations(password1, password2);

  let submitLabel = isReset
    ? disableSubmit
      ? t('resetpwSubmitting')
      : t('resetpwHeading')
    : disableSubmit
      ? t('setpwSubmitting')
      : t('setpwSubmit');

  const handleSave = (data: SetPasswordData) => {
    if (!isReset) {
      setHasPressedSend(true);
      if (!isChecked) {
        return;
      }
    }
    onSubmit(data);
  };

  return (
    <UnauthedPage>
      <Stack space={6}>
        <Stack space={2}>
          {!isReset ? (
            <>
              <Text font="heading1" text={t('setpwWelcome')} center />
              <Text font="regular16" color="purple80" text={t('setpwHeading')} center />
            </>
          ) : (
            <>
              <Text font="heading1" text={t('resetpw')} center />
              <Text font="regular16" color="purple80" text={t('pleaseEnterPassword')} center />
            </>
          )}
        </Stack>
        <Form onSubmit={handleSave} store={formStore}>
          <Stack space={4}>
            <Form.PasswordField
              id="password1"
              label={!isReset ? t('password') : t('newPassword')}
              validators={['required']}
            />
            <PasswordStrengthBar password={password1} />
            <Form.PasswordField
              id="password2"
              label={t('confirmPassword')}
              validators={['required']}
            />
            <ValidationMessages />
            {!isReset && (
              <Stack space={2}>
                <Stack orientation="horizontal" align="center" space={2}>
                  <Checkbox
                    value={isChecked}
                    onChange={() => setIsChecked(!isChecked)}
                    label={t('iAgree')}
                    labelVisible={false}
                  />
                  <CheckboxLabel />
                </Stack>
                {!isChecked && hasPressedSend && (
                  <Text font="regular14" color="red100" text={t('setpwAgreeError')} />
                )}
              </Stack>
            )}
            <Form.Submit
              isDisabled={disableSubmit || !isAllValid}
              text={submitLabel}
              variant="primary-blue"
            />
          </Stack>
        </Form>
      </Stack>
    </UnauthedPage>
  );
};

const CheckboxLabel = () => {
  const { t } = useStore();
  return (
    <Stack space={1} orientation="horizontal" align="center">
      <Text font="regular14" text={t('setpw.agree')} />
      <ActionText.ExternalLink href="https://www.50skills.com/terms-of-use" hoverColor="purple100">
        <LinkText text={t('setpw.terms')} />
      </ActionText.ExternalLink>
      <Text font="regular14" text={t('setpw.and')} />
      <ActionText.ExternalLink
        href="https://www.50skills.com/privacy-policy"
        hoverColor="purple100"
      >
        <LinkText text={t('setpw.privacypolicy')} />
      </ActionText.ExternalLink>
    </Stack>
  );
};

const LinkText = styled(Text)({ lineHeight: '0.9375rem' });
LinkText.defaultProps = { font: 'bold14', color: 'purple100' };

const locale = {
  en: {
    password: 'Password',
    resetpw: 'Reset password',
    pleaseEnterPassword: 'Please enter your new password',
    newPassword: 'New password',
    confirmPassword: 'Confirm password',
    resetpwSubmitting: 'Resetting password...',
    resetpwHeading: 'Reset password',
    setpwSubmitting: 'Setting password...',
    setpwSubmit: 'Set password',
    setpwWelcome: 'Welcome to 50skills',
    setpwHeading: 'Please choose a password',
    setpwAgreeError: 'To sign up you must agree to the terms of service and privacy policy',
    iAgree: 'I agree to the terms of service and privacy policy',
  },
  is: {
    password: 'Lykilorð',
    resetpw: 'Endurstilla lykilorð',
    pleaseEnterPassword: 'Vinsamlegast sláðu inn nýtt lykilorð',
    newPassword: 'Nýtt lykilorð',
    confirmPassword: 'Staðfesta lykilorð',
    resetpwSubmitting: 'Endurstilli lykilorð...',
    resetpwHeading: 'Endurstilla lykilorð',
    setpwSubmitting: 'Set lykilorð...',
    setpwSubmit: 'Vista lykilorð',
    setpwWelcome: 'Velkomi/n/ð/nn í 50skills',
    setpwHeading: 'Vinsamlegast veldu lykilorð',
    setpwAgreeError: 'Til að skrá þig þarftu að samþykkja þjónustuskilmála og persónuverndarstefnu',
    iAgree: 'Ég samþykki þjónustuskilmála og persónuverndarstefnu',
  },
};
